import React, { ReactElement, useState, useEffect, useRef, CSSProperties, ChangeEvent, useCallback } from "react";
import "./AdvancedSettings.css";
import Clipboard from "clipboard";
import defaultTpl from "../../../../assets/templates/ReadBarcodes_Default.json";
import { Tooltip } from "react-tooltip";
import { useSelector, useDispatch } from "react-redux";
import { deepClone, animation, tap2ToTap4 } from "../../../../utils/helpers";
import { changeRegion } from "../../../../store/features/regionSlice";
import { TypeGlobalSettings, updateGlobalSettings } from "../../../../store/features/globalSettingsSlice";
import { PreRef, DivRef, UniteTypeTpl } from "../../../../utils/typesAndMaps";
import { Region } from "../../../../store/features/regionSlice";
// import { updateState } from "../../../../store/features/generalStatesSlice";
import { RootStore } from "../../../../store";
import { Region_2 } from "../../../../utils/globalSettings";
import { warpTemplateFromGlobalSettings } from "../../../../utils/templateUIConfigConverter";

interface AdvancedSettingsProps {
  settingsToggle: boolean,
  showHideAdvancedSettings: () => void,
  isAdvancedSettingsVisible: boolean,
}

const toolTipStyle = {
  width: "160px",
  height: "auto",
  lineHeight: "1.2",
  fontSize: "11px",
  wordBreak: "break-word",
  zIndex: "10",
  padding: "5px",
  fontFamily: "Helvetica Neue,Helvetica,PingFang SC,Hiragino Sans GB,Microsoft YaHei,Noto Sans CJK SC,WenQuanYi Micro Hei,Arial,sans-serif"
} as CSSProperties;

export default function AdvancedSettings({ settingsToggle, showHideAdvancedSettings, isAdvancedSettingsVisible }: AdvancedSettingsProps) {
  const preSettingsRef: PreRef = useRef(null);
  const furtherModesRef: DivRef = useRef(null);
  const [preSettings, setPreSettings] = useState("");
  const oriGlobalSettings = useSelector((store: RootStore) => store.globalSettings);
  const oriRegion = useSelector((store: RootStore) => store.region);
  const generalStates = useSelector((store: RootStore) => store.generalStates);
  const response = useSelector((store: RootStore) => store.decodeResponse);
  const region = deepClone(oriRegion);
  const globalSettings = deepClone(oriGlobalSettings) as TypeGlobalSettings;
  const dispatch = useDispatch();

  const globalSettingsRef = useRef(globalSettings);
  const responseRef = useRef(response);

  const styles = {
    "--display": settingsToggle || !isAdvancedSettingsVisible ? "none" : "block",
  } as CSSProperties;

  useEffect(()=>{
    globalSettingsRef.current = oriGlobalSettings;
    responseRef.current = response;
  }, [oriGlobalSettings, response])

  useEffect(() => {
    if (oriRegion.top === undefined || oriRegion.left === undefined || oriRegion.right === undefined || oriRegion.bottom === undefined) return;
    if (!/px|%$/.test(oriRegion.top) || !/px|%$/.test(oriRegion.left) || !/px|%$/.test(oriRegion.right) || !/px|%$/.test(oriRegion.bottom)) return;
    const copySettings = deepClone(globalSettingsRef.current) as TypeGlobalSettings;
    let regionValue: Region_2;
    regionValue = {
      RegionTop: parseInt(oriRegion.top.replace(/px|%$/, "")),
      RegionLeft: parseInt(oriRegion.left.replace(/px|%$/, "")),
      RegionRight: parseInt(oriRegion.right.replace(/px|%$/, "")),
      RegionBottom: parseInt(oriRegion.bottom.replace(/px|%$/, "")),
      RegionMeasuredByPercentage: oriRegion.isPercentage ? 1 : 0
    };

    if (generalStates.curImgIdx === 4) {
      copySettings.FurtherModes.inside.DPMCodeReadingModes.value = ["DPMCRM_GENERAL", "DPMCRM_SKIP", "DPMCRM_SKIP", "DPMCRM_SKIP", "DPMCRM_SKIP", "DPMCRM_SKIP", "DPMCRM_SKIP", "DPMCRM_SKIP"];
    }

    copySettings.Region.value = regionValue;

    dispatch(updateGlobalSettings({ settings: copySettings }));
  }, [dispatch, oriRegion, generalStates.curImgIdx]);

  const updateRuntimeJsonFromTemplate = useCallback((type: "response" | "globalSettings") => {
    let tpl = defaultTpl as UniteTypeTpl;
    if (type === "response") {
      tpl = responseRef.current.settings!;
    } else {
      tpl = warpTemplateFromGlobalSettings(globalSettingsRef.current);
    }
    const settings = (JSON as any).stringify(tpl, null, 2, function (_: any, value: any) {
      if (value instanceof Array) {
        return JSON.stringify(value);
      } else {
        return value;
      }
    }, 2);
    setPreSettings(settings);
  }, [])

  useEffect(() => {
    updateRuntimeJsonFromTemplate("response");
  }, [response, updateRuntimeJsonFromTemplate]);

  useEffect(() => {
    updateRuntimeJsonFromTemplate("globalSettings");
  }, [oriGlobalSettings, updateRuntimeJsonFromTemplate]);

  const renderAdvancedSettings = (settings: any, index?: number) => {
    let tree: Array<ReactElement> = [];
    for (let item in settings) {
      if (settings[item].type === "slider") {
        tree.push(
          <div className="dbr-as-item slider" key={item}>
            <div className="div-label-container">
              <label data-tooltip-id={item} data-tooltip-content={settings[item].description}>{item}</label>
            </div>
            <Tooltip id={item} style={toolTipStyle} />
            <input
              type="range"
              min={settings[item].min}
              max={settings[item].max}
              value={index !== undefined ? settings[item].value[index] : settings[item].value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => { iptChangeEvent(e, "slider", settings, item, index) }}
            />
            <input
              type="text"
              min={settings[item].min}
              max={settings[item].max}
              value={index !== undefined ? settings[item].value[index] : settings[item].value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => { iptChangeEvent(e, "slider", settings, item, index) }}
            />
          </div>
        )
      } else if (settings[item].type === "checkbox") {
        tree.push(
          <div className="dbr-as-item checkbox" key={item}>
            <div className="div-label-container">
              <label data-tooltip-id={item} data-tooltip-content={settings[item].description}>{item}</label>
            </div>
            <Tooltip id={item} style={toolTipStyle} />
            <input
              type="checkbox"
              checked={index !== undefined ? settings[item].value[index] : settings[item].value}
              onChange={(e: any) => { iptChangeEvent(e, "checkbox", settings, item, index) }}
            />
          </div>
        )
      } else if (settings[item].type === "select") {
        tree.push(
          <div className="dbr-as-item select" key={item}>
            <div className="div-label-container">
              <label>{item}</label>
            </div>
            <div style={{ paddingLeft: "13px" }}>
              <select value={index !== undefined ? settings[item].value[index] : settings[item].value} onChange={(e: ChangeEvent<HTMLSelectElement>) => { selectChangeEvent(e, settings, item, index) }}>
                {
                  Object.keys(settings[item].options).map((option: any) => {
                    return (
                      <option key={option} value={settings[item].options[option]}>{option}</option>
                    )
                  })
                }
              </select>
            </div>
          </div>
        )
      } else if (settings[item].type === "mode") {
        tree.push(
          <div className="dbr-as-item mode" key={item} style={settings[item].style}>
            <div className="div-label-container">
              <label data-tooltip-id={item} data-tooltip-content={settings[item].description}>{item}</label>
            </div>
            <Tooltip id={item} style={toolTipStyle} />
            <div className="arguments" style={{ paddingLeft: "13px" }}>
              {renderSelect(settings[item].modeDisplayCount, settings, item)}
            </div>
          </div>
        )
      } else if (settings[item].type === "inside") {
        tree.push(
          <div className="dbr-as-item inside" key={item}>
            <span style={{ cursor: "pointer" }} onClick={showFurtherModes}><label style={{ cursor: "pointer" }}><strong>{item}</strong></label>
              <svg className="icon" style={{ verticalAlign: "middle" }} viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="3248" width="22" height="12"><path d="M116.736 282.624c8.192 0 16.384 3.072 22.528 9.216l372.736 372.736L884.736 291.84c6.144-6.144 13.824-9.216 22.528-9.216 8.192 0 16.384 3.072 22.528 9.216 12.288 12.288 12.288 32.256 0 44.544L534.528 732.16c-6.144 6.144-13.824 9.216-22.528 9.216-8.192 0-16.384-3.072-22.528-9.216L94.208 336.896c-12.288-12.288-12.288-32.256 0-44.544 6.144-6.144 14.336-9.728 22.528-9.728z" fill="#2C2C2C" p-id="3249"></path></svg>
            </span>
            <div style={{ paddingLeft: "13px", display: "none" }} className="dbr-furtherModes" ref={furtherModesRef}>
              {renderAdvancedSettings(settings[item].inside)}
            </div>
          </div>
        )
      } else if (settings[item].type === "region") {
        tree.push(
          <div className="dbr-as-item region" key={item}>
            <div className="dbr-region-header">
              <label style={{ display: "inline-block", width: "auto", marginRight: "115px" }} data-tooltip-id={item} data-tooltip-content={settings[item].description}>{item}</label>
              <Tooltip id={item} style={toolTipStyle} />
              <input
                type="checkbox"
                checked={oriRegion.isPercentage}
                onChange={changeRegionUnit}
                style={{ marginRight: "5px" }}
              />%
            </div>
            <div className="dbr-region-ipt">
              <div><input
                type="text"
                placeholder={oriRegion.isPercentage ? "top(%)" : "top(px)"}
                value={region.top !== undefined ? region.top : ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => { updateRegion(e, "top") }}
                onBlur={() => { regionBlur("top") }}
                onFocus={() => { regionFocus("top") }} /></div>
              <div><input
                type="text"
                placeholder={oriRegion.isPercentage ? "left(%)" : "left(px)"}
                value={region.left !== undefined ? region.left : ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => { updateRegion(e, "left") }}
                onBlur={() => { regionBlur("left") }}
                onFocus={() => { regionFocus("left") }} />
                <input
                  type="text"
                  placeholder={oriRegion.isPercentage ? "right(%)" : "right(px)"}
                  value={region.right !== undefined ? region.right : ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => { updateRegion(e, "right") }}
                  onBlur={() => { regionBlur("right") }}
                  onFocus={() => { regionFocus("right") }} />
              </div>
              <div><input
                type="text"
                placeholder={oriRegion.isPercentage ? "bottom(%)" : "bottom(px)"}
                value={region.bottom !== undefined ? region.bottom : ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => { updateRegion(e, "bottom") }}
                onBlur={() => { regionBlur("bottom") }}
                onFocus={() => { regionFocus("bottom") }} />
              </div>
            </div>
          </div>
        )
      }
    }
    return tree;
  }

  const renderSelect = (count: number, settings: any, item: string) => {
    let selectTree: Array<ReactElement> = [];
    for (let i = 0; i < count; i++) {
      selectTree.push(
        <div key={i}>
          <select value={settings[item].value[i]} onChange={(e: any) => { selectChangeEvent(e, settings, item, i) }}>
            {
              Object.keys(settings[item].options).map((option: string) => {
                return (
                  <option key={option} value={settings[item].options[option].value}>{option}</option>
                )
              })
            }
          </select>
          {
            Object.keys(settings[item].options).map((option: string) => {
              if (settings[item].options[option].value === settings[item].value[i] && settings[item].options[option].args) {
                return renderArguments(settings[item].options[option].args, i);
              } else {
                return null;
              }
            })
          }
        </div>
      )
    }
    return selectTree;
  }

  const renderArguments = (args: any, index: number) => {
    return (
      <div className="dbr-args" style={{ paddingLeft: "13px" }} key={index}>
        {renderAdvancedSettings(args, index)}
      </div>
    )
  }

  const selectChangeEvent = (e: ChangeEvent<HTMLSelectElement>, settings: any, item: string, index?: number) => {
    const value = isNaN(parseInt(e.target.value)) ? e.target.value : parseInt(e.target.value);
    if (index !== undefined) {
      settings[item].value[index] = value;
      for (let option in settings[item].options) {
        for (let arg in settings[item].options[option].args) {
          settings[item].options[option].args[arg].value[index] = settings[item].options[option].args[arg].default[index];
        }
      }
    } else {
      settings[item].value = value;
    }
    dispatch(updateGlobalSettings({ settings: globalSettings }));
  }

  const iptChangeEvent = (e: ChangeEvent<HTMLInputElement>, type: string, settings: any, item: string, index: number | undefined) => {
    const value = parseInt(e.target.value);
    if (type === "slider") {
      if (index !== undefined) {
        settings[item].value[index] = value;
      } else {
        settings[item].value = value;
      }
    } else if (type === "checkbox") {
      if (index !== undefined) {
        settings[item].value[index] = settings[item].value[index] ? 0 : 1;
      } else {
        settings[item].value = settings[item].value ? 0 : 1;
      }
    }
    dispatch(updateGlobalSettings({ settings: globalSettings }));
  }

  const changeRegionUnit = () => {
    region.isPercentage = !region.isPercentage;
    if (/px|%$/.test(region.top!)) region.top = region.top!.replace(/px|%$/, region.isPercentage ? "%" : "px");
    if (/px|%$/.test(region.left!)) region.left = region.left!.replace(/px|%$/, region.isPercentage ? "%" : "px");
    if (/px|%$/.test(region.right!)) region.right = region.right!.replace(/px|%$/, region.isPercentage ? "%" : "px");
    if (/px|%$/.test(region.bottom!)) region.bottom = region.bottom!.replace(/px|%$/, region.isPercentage ? "%" : "px");
    if (region.isPercentage) {
      region.top = parseInt(region.top!) > 100 ? "100%" : region.top;
      region.left = parseInt(region.left!) > 100 ? "100%" : region.left;
      region.right = parseInt(region.right!) > 100 ? "100%" : region.right;
      region.bottom = parseInt(region.bottom!) > 100 ? "100%" : region.bottom;
    }
    dispatch(changeRegion({ region }));
  }

  const updateRegion = (e: ChangeEvent<HTMLInputElement>, position: Exclude<keyof Region, 'isPercentage'>) => {
    let value: string | string[] = e.target.value
    if (/\D/.test(value)) return;
    if (value.startsWith("0") && value.length > 1) {
      value = value.split("");
      value.splice(0, 1);
      value = value.join("");
    }
    if (oriRegion.isPercentage && parseInt(value) > 100) value = "100";
    region[position] = value;
    dispatch(changeRegion({ region }));
  }

  const regionBlur = (position: Exclude<keyof Region, 'isPercentage'>) => {
    if (!region.top && !region.bottom && !region.left && !region.right) {
      dispatch(changeRegion({
        region: {
          top: "0%",
          left: "0%",
          right: "100%",
          bottom: "100%",
          isPercentage: true
        }
      }));
      return;
    }
    if (!region[position]) return;
    region[position] = region[position] + (oriRegion.isPercentage ? "%" : "px");
    dispatch(changeRegion({ region }));
  }

  const regionFocus = (position: Exclude<keyof Region, 'isPercentage'>) => {
    if (!region[position]) return;
    region[position] = String(parseInt(region[position] as string));
    dispatch(changeRegion({ region }));
  }

  const showFurtherModes = () => {
    animation(furtherModesRef.current!, 4)
  }

  const copyFullSettings = () => {
    const clipboard = new Clipboard(".dbr-copy-settings", {
      text() {
        const settings = tap2ToTap4(preSettingsRef.current!.innerText.replace("Current: \nVersion 9\nVersion 10\n", "").replace("copy\n", ""));
        return settings;
      }
    });
    clipboard.on("success", () => {
      alert("Copied!");
      clipboard.destroy();
    })
    clipboard.on("error", () => {
      alert("Failed!");
      clipboard.destroy();
    })
  }

  const downFullSettings = () => {
    // For tap 4
    const settings = tap2ToTap4(preSettings);
    const jsonBlob = new Blob([settings], { type: "application/json" });
    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(jsonBlob);
    downloadLink.download = `template.json`;
    downloadLink.onclick = () => {
      setTimeout(() => {
        URL.revokeObjectURL(downloadLink.href);
      }, 500);
    };
    downloadLink.click();
  }

  return (
    <div className="dbr-advanced-settings" style={styles}>
      <div className="dbr-as-header">
        <span>Advanced Settings</span>
        <a href="https://www.dynamsoft.com/barcode-reader/docs/core/parameters/reference/image-parameter/?ver=latest" target="_blank" rel="noreferrer" data-tooltip-id="dbr-go-to-doc" data-tooltip-content="Go to documentation">
          <svg className="dbr-document-icon" id="Layer_2" width="24" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 24"><defs><style>{`.cls-1,.cls-2{strokeWidth:"0px";}.cls-2{fill:none;}`}</style></defs><g id="Icons"><path className="cls-2" d="m15,4.5c0,.28.22.5.5.5h3.29c-.51-1.83-1.96-3.28-3.79-3.79v3.29Z" /><path className="cls-2" d="m15.5,6c-.83,0-1.5-.67-1.5-1.5V1.02c-.16-.01-.31-.02-.47-.02H1.5c-.28,0-.5.22-.5.5v21.05c0,.25.2.45.45.45h17.05c.28,0,.5-.22.5-.5V6.48c0-.16-.01-.32-.02-.48h-3.48Zm-11.5,2.28h7c.28,0,.5.22.5.5s-.22.5-.5.5h-7c-.28,0-.5-.22-.5-.5s.22-.5.5-.5Zm12,9H4c-.28,0-.5-.22-.5-.5s.22-.5.5-.5h12c.28,0,.5.22.5.5s-.22.5-.5.5Zm0-4H4c-.28,0-.5-.22-.5-.5s.22-.5.5-.5h12c.28,0,.5.22.5.5s-.22.5-.5.5Z" /><path className="cls-1" d="m19.92,5.5s0,0,0,0c0-.01,0-.02,0-.03-.43-2.77-2.62-4.95-5.38-5.38-.01,0-.02,0-.03,0,0,0,0,0,0,0-.32-.05-.64-.08-.97-.08H1.5C.67,0,0,.67,0,1.5v21.05c0,.8.65,1.45,1.45,1.45h17.05c.83,0,1.5-.67,1.5-1.5V6.48c0-.33-.03-.65-.08-.97Zm-1.13-.5h-3.29c-.28,0-.5-.22-.5-.5V1.21c1.83.51,3.28,1.96,3.79,3.79Zm.21,17.5c0,.28-.22.5-.5.5H1.45c-.25,0-.45-.2-.45-.45V1.5c0-.28.22-.5.5-.5h12.03c.16,0,.32.01.47.02v3.48c0,.83.67,1.5,1.5,1.5h3.48c.01.16.02.31.02.48v16.02Z" /><path className="cls-1" d="m16,16.28H4c-.28,0-.5.22-.5.5s.22.5.5.5h12c.28,0,.5-.22.5-.5s-.22-.5-.5-.5Z" /><path className="cls-1" d="m16,12.28H4c-.28,0-.5.22-.5.5s.22.5.5.5h12c.28,0,.5-.22.5-.5s-.22-.5-.5-.5Z" /><path className="cls-1" d="m4,9.28h7c.28,0,.5-.22.5-.5s-.22-.5-.5-.5h-7c-.28,0-.5.22-.5.5s.22.5.5.5Z" /></g></svg>
        </a>
        <Tooltip id="dbr-go-to-doc" style={{ ...toolTipStyle, width: "auto" }} />
        <svg className="dbr-close-icon" id="Layer_2" width="14" height="14" onClick={showHideAdvancedSettings} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12.57 12.57"><defs><style>{`.cls-1{fill:#323234;stroke-width:0px;}`}</style></defs><g id="Icons"><path className="cls-1" d="m7.88,6.28L12.23,1.93c.44-.44.44-1.16,0-1.6s-1.16-.44-1.6,0l-4.35,4.35L1.93.33C1.49-.11.77-.11.33.33S-.11,1.49.33,1.93l4.35,4.35L.33,10.64c-.44.44-.44,1.16,0,1.6.22.22.51.33.8.33s.58-.11.8-.33l4.35-4.35,4.35,4.35c.22.22.51.33.8.33s.58-.11.8-.33c.44-.44.44-1.16,0-1.6l-4.35-4.35Z" /></g></svg>
      </div>
      <div className="dbr-as-main">
        {renderAdvancedSettings(globalSettings)}
        {/* <div className="dbr-multi-threaded-decoding"><span>Multi-threaded decoding</span><input type="checkbox" onChange={() => { dispatch(updateState({ stateName: "decodingMode", value: !generalStates.decodingMode })) }} /></div> */}
        <div className="dbr-full-settings">
          <div className="dbr-full-settings-header">
            <div className="dbr-full-settings-title">RuntimeSettings</div>
            <div className="dbr-active">Template</div>
          </div>
          <pre className="dbr-full-settings-main" ref={preSettingsRef}>
            <div className="dbr-settings-btns">
              <Tooltip id="dbr-copy-settings" style={{ ...toolTipStyle, width: "auto" }} />
              <button data-tooltip-id="dbr-copy-settings" data-tooltip-content="copy" className="dbr-copy-settings" onClick={copyFullSettings}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 24" width="20" height="24"><g id="a" /><g id="b"><g id="c"><path d="M19.56,3.85L16.15,.44c-.28-.28-.66-.44-1.06-.44H4.5c-.83,0-1.5,.67-1.5,1.5v1.5H1.5c-.83,0-1.5,.67-1.5,1.5V22.5c0,.83,.67,1.5,1.5,1.5H15.5c.83,0,1.5-.67,1.5-1.5v-1.5h1.5c.83,0,1.5-.67,1.5-1.5V4.91c0-.39-.16-.78-.44-1.06Zm-3.56,18.65c0,.28-.22,.5-.5,.5H1.5c-.28,0-.5-.22-.5-.5V4.5c0-.28,.22-.5,.5-.5h1.5v15.5c0,.83,.67,1.5,1.5,1.5h11.5v1.5Zm3-3c0,.28-.22,.5-.5,.5H4.5c-.28,0-.5-.22-.5-.5V1.5c0-.28,.22-.5,.5-.5H15.09c.13,0,.26,.05,.35,.15l3.41,3.41c.09,.09,.15,.22,.15,.35v14.59Z" /></g></g></svg>
              </button>
              <Tooltip id="dbr-download-settings" style={{ ...toolTipStyle, width: "auto" }} />
              <button data-tooltip-id="dbr-download-settings" data-tooltip-content="download" className="dbr-download-settings" onClick={downFullSettings}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="24"><g id="a" /><g id="b"><g id="c"><g><path d="M9.5,23H1.5c-.28,0-.5-.22-.5-.5V1.5c0-.28,.22-.5,.5-.5H14.79c.14,0,.26,.06,.36,.15l3.71,3.7c.09,.09,.15,.22,.15,.35v3.29c0,.28,.22,.5,.5,.5s.5-.22,.5-.5v-3.29c0-.4-.16-.78-.44-1.06L15.86,.45c-.28-.29-.66-.45-1.06-.45H1.5C.67,0,0,.67,0,1.5V22.5c0,.83,.67,1.5,1.5,1.5H9.5c.28,0,.5-.22,.5-.5s-.22-.5-.5-.5Z" /><path d="M6.85,6.65c-.2-.2-.51-.2-.71,0l-3,3c-.2,.2-.2,.51,0,.71l3,3c.09,.09,.22,.15,.35,.15,.13,0,.26-.05,.35-.15,.2-.19,.2-.51,0-.71l-2.65-2.65,2.65-2.65c.2-.2,.2-.51,0-.71Z" /><path d="M9,13c0,.28,.22,.5,.5,.5,.13,0,.26-.05,.35-.15l3-3c.2-.2,.2-.51,0-.71l-3-3c-.2-.2-.51-.2-.71,0s-.2,.51,0,.71l2.65,2.65-2.65,2.65c-.09,.09-.15,.22-.15,.35Z" /><path d="M17.5,11c-3.59,0-6.5,2.91-6.5,6.5s2.91,6.5,6.5,6.5c3.59,0,6.5-2.91,6.5-6.5,0-3.59-2.91-6.5-6.5-6.5Zm0,12c-3.04,0-5.5-2.46-5.5-5.5s2.46-5.5,5.5-5.5c3.04,0,5.5,2.46,5.5,5.5,0,3.04-2.46,5.5-5.5,5.5Z" /><path d="M19.4,17.9l-1.4,1.4v-4.79c0-.28-.22-.5-.5-.5s-.5,.22-.5,.5v4.79l-1.4-1.4c-.2-.2-.51-.2-.71,0s-.2,.51,0,.71l2.25,2.25s.06,.04,.1,.06c.02,.01,.04,.03,.07,.04,.06,.03,.13,.04,.19,.04,.07,0,.13-.01,.19-.04,.03-.01,.04-.03,.07-.04,.03-.02,.07-.04,.09-.06l2.25-2.25c.2-.2,.2-.51,0-.71s-.51-.2-.71,0Z" /></g></g></g></svg>
              </button>
            </div>
            {preSettings}
          </pre>
        </div>
      </div>
    </div>
  )
}